import React from 'react'
import cx from 'classnames'
import { useIntl } from 'intl'

import { track } from 'analytics'
import { WidthContainer } from 'components/layout'
import type { TextStyle, TextColor } from 'components/dataDisplay'
import { HoverablePopover } from 'components/navigation'
import { NavItem } from 'compositions/navigation'

import s from './NavRootItem.scss'


type NavItemProps = {
  data: Navigation.HeaderDesktopSectionItem
  className?: string
  popoverPanelClassName?: string
  style?: TextStyle
  color?: TextColor
  background?: 'faded-pink' | 'vinous'
  hoverColor?: 'gold-30'
  groupOpenedPopoverCountRef?: React.MutableRefObject<number>
  onClick?: () => void
  'data-testid'?: string
} & AllOrNothing<{
  activeColor?: TextColor
  isActive?: boolean
}>

const NavRootItem: React.FunctionComponent<NavItemProps> = (props) => {
  const { className, data, style, color, background, hoverColor, activeColor, isActive,
    popoverPanelClassName, groupOpenedPopoverCountRef, 'data-testid': dataTestId } = props

  const { id, title: sectionTitle, list, subNavLinksPosition, withColumns, bannerComponent } = data
  const intl = useIntl()

  const formattedTitle = intl.formatMessage(sectionTitle)

  const buttonClassNames = cx(className, 'flex cursor-pointer items-center', background && s[`background-${background}`], {
    [`text-${style}`]: style,
    [`hover:text-${hoverColor}`]: hoverColor,
    [`text-${color}`]: color && !isActive,
    [`text-${activeColor}`]: activeColor && isActive,
  })

  const isColumnFlowEnabled = withColumns && list.length > 5
  const isMenuOnRightSide = subNavLinksPosition === 'right'

  return (
    <HoverablePopover className="flex items-stretch" groupOpenedPopoverCountRef={groupOpenedPopoverCountRef}>
      {
        ({ open, close }) => (
          <>
            <HoverablePopover.Button
              className={cx(buttonClassNames, {
                [s.activeButton]: open,
                [`text-${activeColor}`]: open && activeColor,
              })}
              data-testid={dataTestId}
            >
              {formattedTitle}
            </HoverablePopover.Button>
            <HoverablePopover.Overlay withDefaultStyle />
            <HoverablePopover.Panel className={cx(popoverPanelClassName, s.flyout, 'absolute left-0 w-full bg-white py-24 text-black')}>
              <WidthContainer className="flex items-start">
                <div className={cx('w-6/12', isMenuOnRightSide ? 'order-2 pr-96' : 'pr-24')}>
                  <ul className={cx(s.container, 'ml-auto', isColumnFlowEnabled && s.columns)}>
                    {
                      list.map(({ id, title, to, href, toTab, label, content, exclusiveLabel }) => (
                        <li key={id} className="mr-16 w-full">
                          <NavItem
                            className="inline-flex py-12"
                            title={title}
                            to={to}
                            href={href}
                            toTab={toTab}
                            label={label}
                            content={content}
                            exclusiveLabel={exclusiveLabel}
                            color="black"
                            hoverColor="gold-30"
                            activeColor="gold-30"
                            onClick={() => {
                              track('Desktop navigation menu click', {
                                blockName: typeof sectionTitle === 'string' ? sectionTitle : sectionTitle.en,
                                title: typeof title === 'string' ? title : title.en,
                                link: to || toTab || href,
                              })
                              close()
                            }}
                            data-testid={id}
                          />
                        </li>
                      ))
                    }
                  </ul>
                </div>
                <div className={cx('w-6/12', isMenuOnRightSide ? 'order-1 pr-8' : 'pl-8')}>
                  {
                    bannerComponent ? React.createElement(bannerComponent, { placement: id, onClick: close }) : null
                  }
                </div>
              </WidthContainer>
            </HoverablePopover.Panel>
          </>
        )
      }
    </HoverablePopover>
  )
}


export default React.memo(NavRootItem)
